import { useEffect, useRef, useState } from 'react';
import { Funnel, FunnelX } from 'lucide-react';
import { Dropdown } from '@/components/Dropdown';
import { TASK_PRIORITIES, type ColumnFilter, type TaskPriority } from '@/types/api';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  urgent: 'var(--color-priority-urgent)',
  high: 'var(--color-priority-high)',
  normal: 'var(--color-priority-normal)',
  low: 'var(--color-priority-low)',
};

/**
 * One column's filter, opened from its own header.
 *
 * Deliberately per column rather than board-wide. "The urgent things I have not
 * started" is a question about Pending; asking it of all five answers a question
 * nobody had and refetches four columns for nothing.
 *
 * Applied on a button press, not on every checkbox. Each change is a network request,
 * and a menu that refetches while you are still deciding makes the list move under
 * the cursor you are aiming with.
 */
export function ColumnFilterMenu({
  filter,
  tags,
  onApply,
}: {
  filter: ColumnFilter;
  /** Tags actually present on this board — an option that matches nothing is noise. */
  tags: readonly string[];
  onApply: (next: ColumnFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ColumnFilter>(filter);
  const ref = useRef<HTMLDivElement>(null);

  // Reopening shows what is actually applied, not whatever was abandoned last time.
  useEffect(() => {
    if (open) setDraft(filter);
  }, [open, filter]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = Boolean(filter.priority?.length || filter.tag || filter.overdue);

  const togglePriority = (priority: TaskPriority) => {
    const current = draft.priority ?? [];
    const next = current.includes(priority)
      ? current.filter((p) => p !== priority)
      : [...current, priority];
    setDraft({ ...draft, priority: next.length ? next : undefined });
  };

  const apply = (next: ColumnFilter) => {
    onApply(next);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={active ? 'Filter — active' : 'Filter'}
        aria-expanded={open}
        title="Filter this column"
        className="flex size-6 items-center justify-center rounded-md transition-colors"
        style={{
          color: active ? 'var(--accent)' : 'var(--text-faint)',
          background: active ? 'var(--accent-soft)' : 'transparent',
        }}
      >
        {/* FunnelX when something is applied: the icon says both what it does and
            that pressing it will take you to where you can undo it. */}
        {active ? <FunnelX size={13} /> : <Funnel size={13} />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Filter this column"
          className="absolute right-0 top-full z-40 mt-1.5 w-56 rounded-xl border p-3"
          style={{
            borderColor: 'var(--border)',
            background: 'var(--surface)',
            boxShadow: 'var(--shadow-panel)',
          }}
        >
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
             style={{ color: 'var(--text-faint)' }}>
            Priority
          </p>
          <div className="mb-3 flex flex-wrap gap-1">
            {TASK_PRIORITIES.map((priority) => {
              const on = (draft.priority ?? []).includes(priority);
              return (
                <button
                  key={priority}
                  type="button"
                  onClick={() => togglePriority(priority)}
                  aria-pressed={on}
                  className="rounded-md px-1.5 py-0.5 text-[11px] font-medium capitalize"
                  style={{
                    color: on ? PRIORITY_COLOR[priority] : 'var(--text-muted)',
                    background: on
                      ? `color-mix(in oklch, ${PRIORITY_COLOR[priority]} 18%, transparent)`
                      : 'var(--surface-sunken)',
                  }}
                >
                  {priority}
                </button>
              );
            })}
          </div>

          {tags.length > 0 && (
            <>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                 style={{ color: 'var(--text-faint)' }}>
                Tag
              </p>
              <div className="mb-3">
                <Dropdown
                  label="Filter by tag"
                  placeholder="Any tag"
                  value={draft.tag ?? null}
                  options={tags.map((tag) => ({ value: tag, label: tag }))}
                  onChange={(tag) => setDraft({ ...draft, tag: tag ?? undefined })}
                />
              </div>
            </>
          )}

          <label className="mb-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.overdue ?? false}
              onChange={(e) => setDraft({ ...draft, overdue: e.target.checked || undefined })}
            />
            Overdue only
          </label>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => apply(draft)}
              className="min-h-9 flex-1 rounded-lg px-3 text-sm font-medium"
              style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
            >
              Apply
            </button>
            {active && (
              <button
                type="button"
                onClick={() => apply({})}
                className="min-h-9 rounded-lg px-3 text-sm"
                style={{ color: 'var(--text-muted)' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
